import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "../../components/Seo";
import { usePricing, type PricingPlan } from "../../api/hooks";
import { track } from "../../lib/track";
import { LoadingSection } from "../../components/Spinner";
import { MobileCarousel } from "../../components/MobileCarousel";
import clsx from "clsx";

const CATEGORIES = [
  { key: "engagement", label: "Avec engagement", blurb: "Accès illimité, cours + open gym" },
  { key: "sans_engagement", label: "Sans engagement", blurb: "À la séance, sans contrainte" },
  { key: "kids", label: "CrossFit Kids", blurb: "Programme dédié aux 8-12 ans" },
  { key: "sur_devis", label: "Sur devis", blurb: "Coaching individuel & groupes" },
];

function formatPrice(cents?: number | null) {
  if (cents == null) return "Sur devis";
  return `${(cents / 100).toFixed(2).replace(/\.00$/, "")} €`;
}

function descriptionLines(description?: string | null) {
  if (!description) return [];
  return description
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildContactLink(plan: {
  name: string;
  priceCents?: number | null;
  period?: string | null;
  category: string;
}) {
  const isQuote = plan.priceCents == null;
  const priceText = plan.priceCents != null
    ? `${formatPrice(plan.priceCents)}${plan.period ? ` / ${plan.period}` : ""}`
    : "sur devis";
  const subject = isQuote
    ? `Demande de devis — ${plan.name}`
    : `Renseignements — formule ${plan.name}`;
  const message = isQuote
    ? `Bonjour,\n\nJe souhaite recevoir un devis pour la prestation « ${plan.name} ».\n\nMerci de me recontacter pour préciser mon besoin.`
    : `Bonjour,\n\nJe suis intéressé(e) par la formule « ${plan.name} » (${priceText}). Pouvez-vous m'en dire plus ?\n\nMerci.`;
  const params = new URLSearchParams({ subject, message });
  return `/contact?${params.toString()}`;
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="mt-0.5 shrink-0 text-brand-500"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function Pricing() {
  const { data: plans, isLoading } = usePricing();
  const presentCategories = CATEGORIES.filter((cat) =>
    plans?.some((p) => p.category === cat.key),
  );
  const [activeCat, setActiveCat] = useState<string | undefined>();

  useEffect(() => {
    if (!plans?.length) return;
    const sections = document.querySelectorAll<HTMLElement>('[id^="cat-"]');
    if (!sections.length) return;

    const ratios = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target.id, entry.intersectionRatio));
        let bestId: string | undefined;
        let bestRatio = 0;
        ratios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActiveCat(bestId.replace("cat-", ""));
      },
      { rootMargin: "-88px 0px -20% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [plans]);

  return (
    <>
      <Seo
        title="Tarifs"
        description="Découvrez les tarifs du CrossFit Héraclès : formules avec ou sans engagement, Drop-in, carnets, CrossFit Kids."
        path="/tarifs"
      />

      <section className="bg-paper py-10 text-ink-900">
        <div className="container">
          <span className="eyebrow">Tarifs</span>
          <h1 className="h-hero mt-2">Nos formules</h1>
          <p className="mt-4 max-w-2xl text-ink-500">
            Des formules adaptées à tous : engagement, sans engagement, coaching personnel.
            Tarifs réduits pour sans emploi, étudiants, militaires et couples.
          </p>

          {presentCategories.length > 1 && (
            <nav className="mt-6 flex flex-wrap gap-2" aria-label="Aller à une catégorie">
              {presentCategories.map((cat) => (
                <a
                  key={cat.key}
                  href={`#cat-${cat.key}`}
                  className={clsx(
                    "rounded px-4 py-2 text-sm font-semibold uppercase tracking-wide transition",
                    activeCat === cat.key
                      ? "bg-brand-500 text-white"
                      : "border border-ink-300 text-ink-700 hover:border-brand-500 hover:text-brand-500",
                  )}
                >
                  {cat.label}
                </a>
              ))}
            </nav>
          )}
        </div>
      </section>

      {isLoading && <LoadingSection />}

      {CATEGORIES.map((cat) => {
        const items = plans?.filter((p) => p.category === cat.key) ?? [];
        if (!items.length) return null;
        return (
          <section key={cat.key} id={`cat-${cat.key}`} className="section scroll-mt-24">
            <div className="container">
              <h2 className="h-section">{cat.label}</h2>
              <p className="mt-1 text-sm text-ink-500">{cat.blurb}</p>

              {/* Desktop / tablette : grille */}
              <div className="mt-8 hidden gap-6 md:grid md:grid-cols-3 lg:grid-cols-4">
                {items.map((p) => (
                  <PricingCard key={p.id} p={p} />
                ))}
              </div>

              {/* Mobile : carousel horizontal */}
              <div className="mt-8 md:hidden">
                <MobileCarousel
                  items={items}
                  keyFn={(p) => p.id}
                  renderItem={(p) => <PricingCard p={p} />}
                />
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}

function PricingCard({ p }: { p: PricingPlan }) {
  const bullets = descriptionLines(p.description);
  return (
    <div
      className={clsx(
        "card relative flex h-full flex-col",
        p.highlight ? "border-2 border-brand-500 shadow-lg md:-translate-y-1" : "border-ink-100",
      )}
    >
      {p.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">
          Le plus choisi
        </span>
      )}

      <h3 className="font-display text-xl font-bold uppercase">{p.name}</h3>

      {p.priceCents != null ? (
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="text-4xl font-extrabold tracking-tight text-ink-900">
            {formatPrice(p.priceCents)}
          </span>
          {p.period && <span className="text-sm font-medium text-ink-500">/ {p.period}</span>}
        </div>
      ) : (
        <div className="mt-3 text-2xl font-extrabold text-ink-900">Sur devis</div>
      )}

      {bullets.length > 0 && (
        <ul className="mt-4 space-y-2 text-sm text-ink-600">
          {bullets.map((line, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckIcon />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-6">
        <Link
          to={buildContactLink(p)}
          onClick={() =>
            track({ type: "pricing_interest", meta: { plan: p.name, category: p.category } })
          }
          className="btn-primary w-full"
        >
          Je m'intéresse
        </Link>
      </div>
    </div>
  );
}
